// Figma component "Tab bar - Tab":
//   https://www.figma.com/design/sseCNpJYq9VS8RO8p4u6HJ/Ticket-Purchase-Flow?node-id=4827-6429
// Sizes in use:
//   medium (products subnav): node-id=4839-61610, mobile node-id=18577-8465
//   large (account nav): node-id=9491-120288

import type { SxProps, TabsProps, Theme } from '@mui/material';
import { Tabs } from '@mui/material';

export type TabBarSize = 'small' | 'medium' | 'large';

export type TabBarProps = Omit<TabsProps, 'sx'> & {
  size?: TabBarSize;
  /**
   * Below `sm`, run the bar past the `Container` gutters to the screen edges,
   * inset its tabs by the gutter and fade out the overflowing right edge.
   */
  bleed?: boolean;
  sx?: SxProps<Theme>;
};

const sizes = {
  small: { height: 48, gap: 4 },
  medium: { height: { xs: 62, md: 78 }, gap: { xs: 3, md: 4 } },
  large: { height: 96, gap: 6 },
} as const;

const indicatorHeight = 3;

/**
 * Underlined tab bar. Pass plain MUI `Tab` children: the bar styles them. A
 * tab has no horizontal padding, so the indicator spans exactly its label and
 * the spacing between tabs comes from the size's gap.
 */
export const TabBar = ({
  size = 'medium',
  bleed = false,
  sx,
  ...props
}: TabBarProps) => {
  const { height, gap } = sizes[size];
  const sxList = Array.isArray(sx) ? sx : [sx];

  return (
    <Tabs
      {...props}
      sx={[
        (theme) => ({
          minHeight: height,
          '.MuiTabs-indicator': {
            height: indicatorHeight,
            backgroundColor: theme.palette.primary.main,
          },
          '.MuiTabs-flexContainer': { gap },
          '.MuiTab-root': {
            ...theme.typography.body1Medium,
            textTransform: 'none',
            minWidth: 0,
            minHeight: height,
            height,
            px: 0,
            py: 0,
            color: theme.palette.text.secondary,
            '&.Mui-selected': { color: theme.palette.text.primary },
            '@media (hover: hover)': {
              '&:hover': {
                color: theme.palette.text.primary,
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  bottom: 0,
                  height: indicatorHeight,
                  backgroundColor: theme.palette.primary.main,
                },
              },
            },
          },
        }),
        bleed &&
          ((theme) => ({
            [theme.breakpoints.down('sm')]: {
              mx: -2.5,
              maskImage: 'linear-gradient(to left, transparent, black 48px)',
              '.MuiTabs-flexContainer': {
                pl: 2.5,
                // Padding doesn't extend a scroll container's scroll width,
                // so the trailing inset is a spacer after the last tab.
                '&::after': {
                  content: '""',
                  flex: `0 0 ${theme.spacing(2.5)}`,
                },
              },
            },
          })),
        ...sxList,
      ]}
    />
  );
};
